import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Select, TreeSelect, Card, Spin, message, Space, Row, Col, InputNumber } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import hqDisplayGroupService from '../../services/hqDisplayGroupService';
import categoryService from '../../services/categoryService';
// 移除硬编码的验证规则导入，改为使用动态生成
// import { VALIDATION_RULES } from '../../utils/validationRules';
import '../../styles/hqDisplayGroup.css';
import { HQDisplayGroup, DisplayGroupCategory } from '../../types/hqDisplayGroup';
import { Category } from '../../types/category';
import { Item } from '../../types/item';
import ItemListManager from './components/ItemListManager';
import { Rule } from 'antd/es/form';

const { Option } = Select;
const { TextArea } = Input;

const HQDisplayGroupEdit: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryTreeData, setCategoryTreeData] = useState<any[]>([]);
  const [showMainDivision, setShowMainDivision] = useState<boolean>(false);
  const [availableDivisions, setAvailableDivisions] = useState<any[]>([]);
  const [whiteListItems, setWhiteListItems] = useState<Item[]>([]);
  const [blackListItems, setBlackListItems] = useState<Item[]>([]);
  const [itemListManagerVisible, setItemListManagerVisible] = useState<boolean>(false);
  const [currentListType, setCurrentListType] = useState<'white' | 'black'>('white');

  // 动态生成验证规则（支持国际化）
  const getValidationRules = () => {
    return {
      name: [
        { required: true, message: t('hqDisplayGroup.form.nameRequired') },
        { max: 100, message: t('common.validation.maxLength', { max: 100 }) }
      ] as Rule[],
      categories: [
        { required: true, message: t('hqDisplayGroup.form.categoriesRequired') },
        { type: "array" }
      ] as Rule[],
      remark: [
        { max: 500, message: t('common.validation.maxLength', { max: 500 }) }
      ] as Rule[]
    };
  };

  useEffect(() => {
    if (code) {
      fetchDisplayGroupDetail(code);
    }
    fetchCategories();
  }, [code]);
  
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await categoryService.listCategories();
      if (response.success && response.data) {
        setCategories(response.data);
        const treeData = buildCategoryTree(response.data);
        setCategoryTreeData(treeData);
      }
    } catch (error) {
      console.error('Failed to fetch category data:', error);
      message.error(t('hqDisplayGroup.loadFailed'));
    } finally {
      setCategoriesLoading(false);
    }
  };

  const buildCategoryTree = (categories: Category[]) => {
    // 创建一个映射来存储每个层级的节点
    const divisionMap: Record<string, any> = {};
    
    // 遍历所有类目，构建树形结构
    categories.forEach(category => {
      const { divisionCode, divisionName, departmentCode, departmentName, 
        groupCode, groupName, classCode, className, subClassCode, subClassName, id } = category;
      
      // 创建division层级
      if (!divisionMap[divisionCode]) {
        divisionMap[divisionCode] = {
          title: divisionName,
          value: `division-${divisionCode}`,
          key: `division-${divisionCode}`,
          selectable: true, // 允许选择非叶子节点
          children: {}
        };
      }
      
      // 创建groupCode层级
      if (!divisionMap[divisionCode].children[groupCode]) {
        divisionMap[divisionCode].children[groupCode] = {
          title: groupName,
          value: `group-${divisionCode}-${groupCode}`,
          key: `group-${divisionCode}-${groupCode}`,
          selectable: true, // 允许选择非叶子节点
          children: {}
        };
      }
      
      // 创建departmentCode层级
      if (!divisionMap[divisionCode].children[groupCode].children[departmentCode]) {
        divisionMap[divisionCode].children[groupCode].children[departmentCode] = {
          title: departmentName,
          value: `department-${divisionCode}-${groupCode}-${departmentCode}`,
          key: `department-${divisionCode}-${groupCode}-${departmentCode}`,
          selectable: true, // 允许选择非叶子节点
          children: {}
        };
      }
      
      // 创建class层级
      if (!divisionMap[divisionCode].children[groupCode].children[departmentCode].children[classCode]) {
        divisionMap[divisionCode].children[groupCode].children[departmentCode].children[classCode] = {
          title: className,
          value: `class-${divisionCode}-${groupCode}-${departmentCode}-${classCode}`,
          key: `class-${divisionCode}-${groupCode}-${departmentCode}-${classCode}`,
          selectable: true, // 允许选择非叶子节点
          children: []
        };
      }
      
      // 添加subClass作为叶子节点
      divisionMap[divisionCode].children[groupCode].children[departmentCode].children[classCode].children.push({
        title: subClassName,
        value: id,
        key: id,
        isLeaf: true,
        selectable: true // 明确设置叶子节点可选
      });
    });
    
    // 将嵌套对象转换为树形数组结构
    const result: any[] = [];
    
    Object.values(divisionMap).forEach((division: any) => {
      const divisionNode = { ...division, children: [] };
      
      Object.values(division.children).forEach((department: any) => {
        const departmentNode = { ...department, children: [] };
        
        Object.values(department.children).forEach((section: any) => {
          const sectionNode = { ...section, children: [] };
          
          Object.values(section.children).forEach((classNode: any) => {
            sectionNode.children.push({
              ...classNode,
              children: classNode.children
            });
          });
          
          departmentNode.children.push(sectionNode);
        });
        
        divisionNode.children.push(departmentNode);
      });
      
      result.push(divisionNode);
    });
    
    return result;
  };

  const fetchDisplayGroupDetail = async (code: string) => {
    try {
      setLoading(true);
      const response = await hqDisplayGroupService.getHQDisplayGroupByCode(code);
      
      if (response.success && response.data) {
        const data = response.data;
        
        // 转换数据格式以适应表单
        form.setFieldsValue({
          code: data.code,
          name: data.name,
          status: data.status,
          mainDivision: data.mainDivision,
          categoryIds: data.subClassSets.map(cat => cat.categoryId),
          storeGroupIds: [], // 需要根据实际数据结构调整
          remark: data.remark,
          saleDays: data.saleDays || 0,
        });
        
        // 设置黑白名单
        setWhiteListItems(data.whiteListItems || []);
        setBlackListItems(data.blackListItems || []);
        
        // 检查是否需要显示主要事业部字段
        if (data.subClassSets && data.subClassSets.length > 0) {
          const divisionMap = new Map();
          data.subClassSets?.forEach(cat => {
            if (cat.divisionCode) {
              divisionMap.set(cat.divisionCode, {
                divisionCode: cat.divisionCode,
                divisionName: cat.divisionName || cat.divisionCode
              });
            }
          });
          
          // 转换为数组
          const divisionsArray = Array.from(divisionMap.values());

          setAvailableDivisions(divisionsArray);
          
          // 如果跨多个事业部，显示主要事业部字段
          setShowMainDivision(divisionsArray.length > 1);
        }
      } else {
        message.error(response.errorMessage || t('hqDisplayGroup.fetchDetailFailed'));
      }
    } catch (error) {
      console.error('Failed to fetch display group details:', error);
      message.error(t('hqDisplayGroup.fetchDetailFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      
      // 获取当前表单数据
      const currentData = form.getFieldsValue();
      
      // 构建更新对象 - 只包含需要更新的字段
      const displayGroup: Partial<HQDisplayGroup> = {
        code: currentData.code,
        name: values.name,
        status: values.status,
        mainDivision: values.mainDivision || '',
        subClassSets: values.categoryIds.map((id: number) => {
          const category = categories.find(cat => cat.id === id);
          return category ? {
            categoryId: category.id,
            divisionCode: category.divisionCode,
            divisionName: category.divisionName,
            departmentCode: category.departmentCode,
            departmentName: category.departmentName,
            groupCode: category.groupCode,
            groupName: category.groupName,
            classCode: category.classCode,
            className: category.className,
            subClassCode: category.subClassCode,
            subClassName: category.subClassName
          } : null;
        }),
        whiteListItems: whiteListItems
          ? whiteListItems.map((item) => item.code)
          : [],
        blackListItems: blackListItems
          ? blackListItems.map((item) => item.code)
          : [],
        remark: values.remark,
        saleDays: values.saleDays || 0,
      };
      
      const response = await hqDisplayGroupService.updateHQDisplayGroup(code!, displayGroup);
      
      if (response.success) {
        message.success(t('hqDisplayGroup.updateSuccess'));
        navigate(`/hq-display-groups/${code}`);
      } else {
        message.error(response.errorMessage || t('hqDisplayGroup.updateFailed'));
      }
    } catch (error) {
      console.error('Update failed:', error);
      message.error(error.message || t('hqDisplayGroup.updateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryIds = (categoryIds: number[]) => {
   return (
    categoryIds
      ?.map((id) => {
        const category = categories.find((cat) => cat.id === id);
        return category ? category.subClassCode : null;
      })
      .filter((item) => item !== null) || []
   );
  };
  const handleBack = () => {
    navigate('/hq-display-groups');
  };

  return (
    <div className="hq-display-group-form">
      <div className="form-header">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          {t('hqDisplayGroup.backToList')}
        </Button>
        <h2>{t('hqDisplayGroup.edit')}</h2>
      </div>

      <Card className="form-card">
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <div className="form-row">
              <Form.Item
                name="code"
                label={t('hqDisplayGroup.code')}
                className="form-col"
              >
                <Input disabled />
              </Form.Item>

              <Form.Item
                name="name"
                label={t('hqDisplayGroup.name')}
                rules={getValidationRules().name}
                className="form-col"
              >
                <Input placeholder={t('hqDisplayGroup.form.namePlaceholder')} maxLength={50} />
              </Form.Item>
            </div>

            <div className="form-row">
              <Form.Item
                name="status"
                label={t('common.status')}
                className="form-col"
              >
                <Select disabled>
                  <Option value="ENABLE">{t('hqDisplayGroup.status.active')}</Option>
                  <Option value="DISABLE">{t('hqDisplayGroup.status.inactive')}</Option>
                  <Option value="Pending">{t('hqDisplayGroup.status.pending')}</Option>
                  <Option value="DRAFT">{t('hqDisplayGroup.status.draft', 'Draft')}</Option>
                </Select>
              </Form.Item>

            </div>

            <Form.Item
              name="categoryIds"
              label={t('hqDisplayGroup.associatedCategories')}
              rules={getValidationRules().categories}
              extra={t('hqDisplayGroup.form.categoriesHelp')}
            >
              <TreeSelect
                treeData={categoryTreeData}
                treeCheckable={true}
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                placeholder={t('hqDisplayGroup.form.selectCategories')}
                style={{ width: '100%' }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                loading={categoriesLoading}
                treeDefaultExpandAll
                allowClear
                showSearch
                treeNodeFilterProp="title"
                maxTagCount={0}
                maxTagPlaceholder={() => {
                  const selectedCount = form.getFieldValue('categoryIds')?.length || 0;
                  return selectedCount > 0
                    ? t("hqDisplayGroup.selectedCount", { count: selectedCount })
                    : "";
                }}
                onSelect={(selectedKeys, info) => {
                  // 处理选中节点，但不做任何操作，由onChange处理
                }}
                onDeselect={(selectedKeys, info) => {
                  // 处理取消选中节点，但不做任何操作，由onChange处理
                }}
                onChange={(value) => {
                  // 根据节点是否有children来判断是否可选
                  if (Array.isArray(value)) {
                    // 保留所有可选节点的值
                    // 这里不再通过前缀过滤，而是在buildCategoryTree中通过设置selectable属性控制
                    form.setFieldsValue({ categoryIds: value });
                    
                    // 获取所有选中的类目
                    const selectedCategories = categories.filter(cat => value.includes(cat.id));
                    
                    // 检查是否所有类目都属于同一个Division
                    if (selectedCategories.length > 0) {
                      // 获取所有选中类目关联的事业部
                      // 创建一个Map来存储唯一的division信息
                      const divisionMap = new Map();
                      selectedCategories.forEach(cat => {
                        if (cat.divisionCode) {
                          divisionMap.set(cat.divisionCode, {
                            divisionCode: cat.divisionCode,
                            divisionName: cat.divisionName || cat.divisionCode
                          });
                        }
                      });
                      
                      // 转换为数组
                      const divisionsArray = Array.from(divisionMap.values());

                      // 更新可选的主要事业部列表
                      setAvailableDivisions(divisionsArray);
                      
                      // 如果只有一个Division，自动设置为mainDivision并隐藏主要事业部字段
                      if (divisionsArray.length === 1) {
                        const division = divisionsArray[0];
                        form.setFieldsValue({ mainDivision: division.divisionCode });
                        setShowMainDivision(false);
                      } else {
                        // 如果跨多个Division，显示主要事业部字段并设为必填
                        setShowMainDivision(true);
                        
                        // 检查当前选择的主要事业部是否在可选列表中
                        const currentMainDivision = form.getFieldValue('mainDivision');
                        if (currentMainDivision && !divisionsArray.some(div => div.divisionCode === currentMainDivision)) {
                          // 如果不在可选列表中，清空选择
                          form.setFieldsValue({ mainDivision: undefined });
                        }
                      }
                    } else {
                      // 如果没有选择类目，隐藏主要事业部字段
                      setShowMainDivision(false);
                      setAvailableDivisions([]);
                      form.setFieldsValue({ mainDivision: undefined });
                    }
                  }
                }}
              />
            </Form.Item>

            {showMainDivision && (
                <Form.Item
                  name="mainDivision"
                  label={t('hqDisplayGroup.mainDivision')}
                  rules={[{
                    required: true,
                    message: t('hqDisplayGroup.form.mainDivisionRequired')
                  }]}
                  className="form-col"
                >
                  <Select placeholder={t('hqDisplayGroup.form.selectMainDivision')} allowClear>
                    {availableDivisions.map(division => (
                    <Option key={division.divisionCode} value={division.divisionCode}>
                      {division.divisionName}
                    </Option>
                  ))}
                  </Select>
                </Form.Item>
              )}
            
            <Form.Item
              name="saleDays"
              label={t('hqDisplayGroup.saleDays')}
              className="form-col"
              rules={[
                {
                  required: true,
                  message: t('hqDisplayGroup.form.saleDaysPlaceholder'),
                },
              ]}
            >
               <InputNumber
                  placeholder={t('hqDisplayGroup.form.saleDaysPlaceholder')} 
                  min={1}
                  max={15}
                  disabled={true}
                  style={{ width: "100%" }}
                />
            </Form.Item>

            <Form.Item label={t('hqDisplayGroup.itemManagement.title')}>
              <Row gutter={16}>
                <Col span={12}>
                  <Button 
                    onClick={() => {
                      setCurrentListType('white');
                      setItemListManagerVisible(true);
                    }}
                    icon={<ShoppingOutlined />}
                    style={{ width: '100%' }}
                  >
                    {t('hqDisplayGroup.itemManagement.manageWhitelist', { count: whiteListItems.length })}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button 
                    onClick={() => {
                      setCurrentListType('black');
                      setItemListManagerVisible(true);
                    }}
                    icon={<ShoppingOutlined />}
                    style={{ width: '100%' }}
                  >
                    {t('hqDisplayGroup.itemManagement.manageBlacklist', { count: blackListItems.length })}
                  </Button>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              name="remark"
              label={t('hqDisplayGroup.remark')}
              rules={getValidationRules().remark}
            >
              <TextArea rows={4} placeholder={t('hqDisplayGroup.form.remarkPlaceholder')} maxLength={500} />
            </Form.Item>

            <Form.Item className="form-actions">
              <Space>
                <Button onClick={handleBack}>{t('hqDisplayGroup.cancel')}</Button>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={submitting}>
                  {t('hqDisplayGroup.save')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>

      {/* 黑白名单管理弹窗 */}
      <ItemListManager
        visible={itemListManagerVisible}
        onClose={() => setItemListManagerVisible(false)}
        whiteListItems={whiteListItems}
        blackListItems={blackListItems}
        onWhiteListChange={setWhiteListItems}
        onBlackListChange={setBlackListItems}
        mainDivision={form.getFieldValue("mainDivision")}
        categoryIds={getCategoryIds(form.getFieldValue("categoryIds"))}
        currentListType={currentListType}
      />
    </div>
  );
};

export default HQDisplayGroupEdit;